import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, ExternalLink, Link2, ShoppingCart } from 'lucide-react';
import { apiService } from '../../services/api';
import CadThumb from '../ProjectPage/CadThumb';

// Fiche d'une pièce du PLM : ses conceptions Fusion (vignette, version, masse), les projets où elle apparaît dans une
// nomenclature Fusion, et ses achats. Les liens se font depuis l'onglet PLM des projets (ou par Nexus).
const ItemLinksCard = ({ itemId }) => {
  const [links, setLinks] = useState(null);

  useEffect(() => {
    if (!itemId) return;
    setLinks(null);
    apiService.get(`/inventory/items/${itemId}/links`).then(setLinks).catch(() => setLinks({ designs: [], parts: [], purchases: [] }));
  }, [itemId]);

  if (!links) return null;
  const empty = !links.designs.length && !links.parts.length && !links.purchases.length;
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm space-y-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase text-gray-500"><Link2 className="h-4 w-4" /> CAO et achats</h3>
      {empty && (
        <p className="text-sm text-slate-400">
          Pas encore liée. Dans l&apos;onglet PLM d&apos;un projet, Deepflow propose de lier ses pièces Fusion et ses achats à cette pièce.
        </p>
      )}
      {links.designs.map((d) => (
        <div key={d.link_id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-2">
          <CadThumb projectId={d.project_id} itemId={d.cad_item_id} version={d.latest} available={Boolean(d.latest)} className="h-14 w-14" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-800"><Box className="mr-1 inline h-3.5 w-3.5 text-indigo-500" />{d.name}</p>
            <p className="truncate text-xs text-slate-500">
              {d.cad_version ? `v${d.cad_version} (figée)` : d.latest ? `dernière version : v${d.latest}` : 'version suivie : la dernière'}
              {d.mass ? ` · ${Number(d.mass).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${d.mass_unit || ''}` : ''}
              {d.parts ? ` · ${d.parts} pièces` : ''}
            </p>
            <Link to={`/project/${d.project_id}?tab=materiel`} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">{d.project}</Link>
          </div>
          {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" title="Ouvrir dans Fusion" className="text-slate-400 hover:text-indigo-600"><ExternalLink size={14} /></a>}
        </div>
      ))}
      {links.parts.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold text-slate-500">Dans les nomenclatures Fusion</p>
          <div className="flex flex-wrap gap-1.5">
            {links.parts.map((p) => (
              <Link key={p.link_id} to={`/project/${p.project_id}?tab=materiel`}
                className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 hover:bg-indigo-100">
                {p.project} · {p.name}
              </Link>
            ))}
          </div>
        </div>
      )}
      {links.purchases.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold text-slate-500">Achats</p>
          <ul className="space-y-1">
            {links.purchases.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-xs text-slate-600">
                <ShoppingCart className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <span className="truncate">{p.quantity ? `${p.quantity}× ` : ''}{p.designation} — {p.vendor}, {p.date}</span>
                {p.total != null && <span className="ml-auto shrink-0 tabular-nums">{Number(p.total).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ItemLinksCard;
